// Shared registry so a full page-content swap (assets/page-transitions.js) can cleanly cancel
// every scroll/resize listener a previous page visit registered, regardless of which function
// below created it — a listener bound via a signal isn't removed just because the code that
// added it goes out of scope, only when that signal is actually aborted. Centralizing this here
// means product-carousel.js, mission-story.js, and any future caller of initializeScrollFocus /
// initializeTrackCentering / initializeScrollZoomAnimationTrigger get cleanup for free just by
// calling them again — they don't need to track or dispose of anything themselves.
window.CowalkiesAnimations = window.CowalkiesAnimations || {
  _controllers: [],
  track: function (controller) {
    if (controller) this._controllers.push(controller);
    return controller;
  },
  resetListeners: function () {
    this._controllers.forEach(function (c) {
      c.abort();
    });
    this._controllers = [];
  },
};

// --focus-scale is written on every animation frame while the page scrolls (initializeScrollFocus
// below). As a plain custom property it is *inherited*, so each write invalidates the computed
// style of the whole subtree under the card — hundreds of elements on the product page — even
// though only the card's own `transform: scale(var(--focus-scale))` ever reads it. Registering it
// as a non-inherited <number> limits that to the card itself. Measured on the product page with
// CPU throttling while an accordion opens during a scroll (what a phone does as a section grows
// under the finger): frames over 20ms went from ~13 to ~0.4 per run. Every reader sets it on the
// same element it reads it on, so nothing depends on inheritance (computed transforms checked
// identical on home, catalog, product and cart). Browsers without registerProperty keep the old
// inherited behaviour.
try {
  if (window.CSS && CSS.registerProperty) {
    CSS.registerProperty({ name: '--focus-scale', syntax: '<number>', inherits: false, initialValue: '1' });
  }
} catch (e) {
  // Already registered (script evaluated twice) — nothing to do.
}

const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const GROW_IN_TRIGGER_CLASSNAME = 'js-grow-observe';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -10px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Separator grow-in logic — its own observer (not the shared one above) so its trigger point
// can be tuned independently: a separator needs to fire once it's solidly inside the viewport
// (not just peeking at the very edge), or the reveal happens in peripheral vision and goes
// unnoticed. Same once-only semantics as the shared observer (unobserve after first reveal).
function onGrowIntersection(elements, observer) {
  elements.forEach((element) => {
    if (element.isIntersecting) {
      element.target.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      observer.unobserve(element.target);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    }
  });
}

function initializeGrowInTrigger(rootEl = document) {
  if (Shopify.designMode) return;

  const growTriggerElements = Array.from(rootEl.getElementsByClassName(GROW_IN_TRIGGER_CLASSNAME));
  if (growTriggerElements.length === 0) return;

  // Positive bottom margin extends the "visible" zone below the real viewport, so this fires
  // while the element is still approaching from below rather than waiting until it's already
  // solidly on screen — reads as "ready by the time you get here" instead of a delayed reveal.
  const observer = new IntersectionObserver(onGrowIntersection, {
    rootMargin: '0px 0px 300px 0px',
  });
  growTriggerElements.forEach((element) => observer.observe(element));
}

// Scroll focus: whichever element sits closest to the center of its scroll container gets
// emphasized (scaled up a touch), everything else recedes (scaled down a touch) — continuously
// tied to scroll position rather than a discrete trigger, so it reads as "what am I looking at
// right now" instead of a one-off reveal. Shared by the page's top-level blocks (vertical,
// window as the container) and each carousel's own cards (horizontal, the track as the
// container) — same technique, just a different axis/container per caller. Any future card
// anywhere on the page opts in the same way (see initializePageScrollFocus below) — nothing
// here is specific to today's three blocks.
//
// Deliberately eased in JS rather than with a CSS transition: a transition retargets itself
// every time the scroll handler writes a new value (up to 60x/second), and two independent
// timing systems fighting over the same property is exactly what read as sharp/twitchy during
// a fast or trackpad-smooth scroll. Instead each element's current scale chases its target by
// a fraction every animation frame (classic exponential smoothing) — a single, continuous,
// self-consistent motion no matter how choppy the underlying scroll events are.
function initializeScrollFocus(elements, options) {
  if (!elements || elements.length === 0) return null;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

  const axis = (options && options.axis) === 'x' ? 'x' : 'y';
  const minScale = (options && options.minScale) || 0.95;
  const maxScale = (options && options.maxScale) || 1;
  const scrollContainer = (options && options.scrollContainer) || window;
  // Optional per-frame range: lets a card whose height changes after load (an expanding tab)
  // keep a constant-pixel swing instead of the range measured once while it was collapsed.
  const getRange = options && options.getRange;
  // Fraction (0–1) of the distance-to-edge that stays at full scale before shrinking starts —
  // a "dead zone" around center so a card doesn't start receding the instant it nudges off
  // center, only once it's meaningfully on its way out.
  const delay = Math.min(Math.max((options && options.delay) || 0, 0), 0.9);

  const EASE = 0.18;
  const SETTLE_THRESHOLD = 0.0005;
  const state = elements.map((element) => ({ element, current: null, target: maxScale }));
  let rafId = null;

  function computeTargets() {
    const viewport =
      scrollContainer === window
        ? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
        : scrollContainer.getBoundingClientRect();
    const viewportSize = axis === 'y' ? viewport.height : viewport.width;
    const viewportCenter = axis === 'y' ? viewport.top + viewportSize / 2 : viewport.left + viewportSize / 2;

    state.forEach((entry) => {
      const r = entry.element.getBoundingClientRect();
      const elSize = axis === 'y' ? r.height : r.width;
      const elCenter = axis === 'y' ? r.top + elSize / 2 : r.left + elSize / 2;
      // elSize/2 here is the correct term geometrically — it's what makes proximity hit 0 right
      // as the element finishes scrolling out of view, not some arbitrary point while it's
      // still mostly on screen. Dropping it entirely (an earlier attempt at this fix) broke
      // that: tall sections like the carousel or footer panel started hitting their minimum
      // scale while still largely visible, since distance can exceed viewportSize/2 well before
      // a tall element has actually left the viewport. The actual complaint — a *short*
      // element's proximity swinging from 1 to 0 over very few scroll pixels — comes from
      // elSize/2 being tiny for a short element, not from the term existing at all. Flooring it
      // gives short elements the same reasonably-wide falloff zone as everything else while
      // leaving tall elements' own (larger, already-correct) size term untouched.
      const MIN_SIZE_BONUS = 200;
      const maxDistance = viewportSize / 2 + Math.max(elSize / 2, MIN_SIZE_BONUS);
      const distance = Math.abs(elCenter - viewportCenter);
      const threshold = maxDistance * delay;
      let proximity = 1;
      if (maxDistance > 0) {
        proximity = distance <= threshold ? 1 : 1 - Math.min((distance - threshold) / (maxDistance - threshold), 1);
      }
      const range = getRange ? getRange(entry.element, r) : { minScale, maxScale };
      entry.target = range.minScale + proximity * (range.maxScale - range.minScale);
      if (entry.current === null) entry.current = entry.target;
    });
  }

  function tick() {
    let stillMoving = false;
    state.forEach((entry) => {
      const delta = entry.target - entry.current;
      if (Math.abs(delta) > SETTLE_THRESHOLD) {
        entry.current += delta * EASE;
        stillMoving = true;
      } else {
        entry.current = entry.target;
      }
      entry.element.style.setProperty('--focus-scale', entry.current.toFixed(4));
    });
    rafId = stillMoving ? requestAnimationFrame(tick) : null;
  }

  function requestTick() {
    computeTargets();
    if (rafId === null) rafId = requestAnimationFrame(tick);
  }

  const controller = new AbortController();
  (scrollContainer === window ? window : scrollContainer).addEventListener('scroll', requestTick, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener('resize', requestTick, { passive: true, signal: controller.signal });
  requestTick();
  return window.CowalkiesAnimations.track(controller);
}

// Global by construction: this queries the whole document for the marker attribute rather
// than naming specific sections, so any future card opts in just by adding
// data-scroll-focus="page" (plus the matching transform/--focus-scale CSS) — no changes needed
// here when more cards are added. Re-scanning on Shopify's section events means a card added
// live in the theme editor is picked up immediately too.
//
// Scale range is computed per element rather than shared, and on purpose: a flat percentage
// (the old minScale/maxScale: 0.97/1.015 for every card) shrinks a tall card — the product
// carousel — by far more pixels than a short one — the header card — purely because 3% of a
// bigger height is a bigger number. That reads as inconsistent spacing between stacked cards
// even though the gap between their boxes is identical (see .ai-card-stack in base.css).
// Normalizing to a constant pixel amount instead means every card breathes by the same visual
// amount regardless of its own height, so the gap on either side of it looks the same too.
function initializePageScrollFocus() {
  initializePageScrollFocusFor(document.querySelectorAll('[data-scroll-focus="page"]'));
}

// Wires just the given elements — for markup injected after load (e.g. product recommendations).
function initializePageScrollFocusFor(nodes) {
  const elements = Array.from(nodes);
  if (elements.length === 0) return;

  const SHRINK_PX = 8;
  const GROW_PX = 4;
  // Floors the height this scale range is computed from — without it, SHRINK_PX/GROW_PX divided
  // by a *short* element's real height (a button row, a folded/collapsed card) blows past a tall
  // card's own barely-there swing and slams straight into the 0.9/1.08 caps below, so the same
  // "gentle breathing" effect reads as a sharp, snappy pop on anything short. 200 mirrors the same
  // floor initializeScrollFocus's own computeTargets already applies to the *falloff distance* for
  // exactly this reason (MIN_SIZE_BONUS) — short and tall elements now land in the same
  // proportional neighborhood instead of short ones hitting a hard ceiling tall ones never reach.
  const MIN_HEIGHT_FOR_SCALE = 200;
  // Read live (from the rect computeTargets already measured) rather than once at load, so a card
  // that grows later — mission-story's expanding tabs — keeps the same pixel swing as every other card.
  function getRange(element, rect) {
    const scaleHeight = Math.max(rect.height || 1, MIN_HEIGHT_FOR_SCALE);
    return {
      minScale: Math.max(1 - SHRINK_PX / scaleHeight, 0.9),
      maxScale: Math.min(1 + GROW_PX / scaleHeight, 1.08),
    };
  }
  elements.forEach((element) => {
    initializeScrollFocus([element], { axis: 'y', getRange });
  });
}

window.initializePageScrollFocusFor = initializePageScrollFocusFor;

// Center a horizontally-scrolling track's content when it doesn't actually need to scroll
// (e.g. a product carousel with only one or two items on a wide desktop viewport) — left-
// aligned-with-a-big-empty-gap reads as unfinished, not intentional. Toggles a class rather
// than hardcoding justify-content directly, so the track keeps its normal flex-start/scrollable
// behavior the instant there's enough content to actually need scrolling — centering an
// overflowing flex container is unreliable across browsers (the start of the content can end
// up unreachable by scroll), so this only ever centers when nothing would be cut off.
function initializeTrackCentering(track, className) {
  if (!track) return;
  className = className || 'is-centered';

  function update() {
    var fits = track.scrollWidth <= track.clientWidth + 1;
    track.classList.toggle(className, fits);
  }

  update();
  const controller = new AbortController();
  window.addEventListener('resize', throttle(update), { passive: true, signal: controller.signal });

  // Re-check after content that affects width settles — e.g. a carousel card's expand/collapse
  // transition, or images finishing layout. A ResizeObserver on the track catches both without
  // the caller needing to know when those happen. Tied to the same controller as the resize
  // listener above via the signal's own 'abort' event, so one abort() call tears down both.
  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(track);
    controller.signal.addEventListener('abort', function () {
      resizeObserver.disconnect();
    });
  }
  window.CowalkiesAnimations.track(controller);
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  const controller = new AbortController();

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);
    controller.signal.addEventListener('abort', function () {
      observer.disconnect();
    });

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true, signal: controller.signal }
    );
  });
  window.CowalkiesAnimations.track(controller);
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeGrowInTrigger();
  initializePageScrollFocus();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
  // A card added live in the editor (or an existing one reordered) needs to be picked up by
  // the page-level focus system without a full reload.
  document.addEventListener('shopify:section:load', initializePageScrollFocus);
  document.addEventListener('shopify:section:reorder', initializePageScrollFocus);
}
